import React, { useState } from 'react';
import {
  EyeIcon,
  EyeSlashIcon,
  ArrowLeftIcon,
  EnvelopeIcon,
  InformationCircleIcon,
} from '@heroicons/react/24/outline';

import logo from 'components/Header/nome.svg';

/* ============================================================
   Shared primitives for the LoginModal flow (main / forgot /
   confirm-email views). Ported from design_handoff_login_v1.
   Styling uses the cream-paper tokens in tailwind.config.js.
   ============================================================ */

/* Brand header band carrying the there2b mark into the moment of
   sign-in. Brand-yellow band with the wordmark rendered white
   (brightness-0 invert recolours the single-fill SVG). */
export function BrandHeader() {
  return (
    <div className="relative h-[120px] sm:h-[140px] overflow-hidden bg-brand flex items-center px-6 sm:px-9">
      {/* faint glow, top-left — keeps the band from feeling flat */}
      <div
        className="absolute inset-0 opacity-50"
        style={{
          background:
            'radial-gradient(ellipse 70% 90% at 0% 0%, rgba(255,255,255,0.35), transparent 60%)',
        }}
      />
      <img
        src={logo}
        alt="there2b"
        className="relative h-12 sm:h-14 w-auto brightness-0 invert mt-16 -ml-4"
      />
    </div>
  );
}

/* Labelled text field — Manrope uppercase label + white input with a
   focus ring. `forgotLabel`/`onForgot` render the inline help link in
   the label row (password field). */
export function LoginField({
  label,
  type = 'text',
  value,
  onChange,
  placeholder,
  autoComplete,
  forgotLabel,
  onForgot,
}) {
  const [show, setShow] = useState(false);
  const isPassword = type === 'password';
  const inputType = isPassword ? (show ? 'text' : 'password') : type;

  return (
    <div className="flex flex-col gap-1.5">
      <div className="flex items-center justify-between gap-3">
        <span className="font-display text-[11px] font-bold uppercase tracking-[0.12em] text-muted">
          {label}
        </span>
        {forgotLabel && (
          <button
            type="button"
            onClick={onForgot}
            aria-label={forgotLabel}
            className="text-[12.5px] font-semibold text-muted-2 hover:text-ink hover:underline underline-offset-[3px] whitespace-nowrap"
          >
            {forgotLabel}
          </button>
        )}
      </div>
      <div className="relative">
        <input
          type={inputType}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          autoComplete={autoComplete}
          required
          className={[
            'w-full appearance-none bg-white border border-line rounded-xl',
            'px-4 py-3.5 text-[15px] text-ink outline-none transition',
            'placeholder:text-muted',
            'focus:border-ink focus:ring-[3px] focus:ring-ink/[0.08]',
            isPassword ? 'pr-12' : '',
          ].join(' ')}
        />
        {isPassword && (
          <button
            type="button"
            onClick={() => setShow((s) => !s)}
            aria-label={show ? 'Hide password' : 'Show password'}
            className="absolute top-1/2 right-2 -translate-y-1/2 grid place-items-center w-9 h-9
                       rounded-full text-muted-2 hover:bg-paper-2 hover:text-ink"
          >
            {show ? (
              <EyeSlashIcon className="w-[18px] h-[18px]" />
            ) : (
              <EyeIcon className="w-[18px] h-[18px]" />
            )}
          </button>
        )}
      </div>
    </div>
  );
}

export function ErrorBanner({ children }) {
  return (
    <div
      role="alert"
      aria-live="polite"
      className="flex gap-2.5 items-start px-3.5 py-3 rounded-xl bg-bad-soft
                 border border-[rgba(161,46,46,0.18)] text-bad text-[13px] leading-[1.4]"
    >
      <InformationCircleIcon className="w-4 h-4 mt-0.5 shrink-0" />
      <span>{children}</span>
    </div>
  );
}

export function BackButton({ label, onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="inline-flex items-center gap-1.5 mb-[18px] text-[12.5px] font-semibold
                 text-muted-2 hover:text-ink whitespace-nowrap"
    >
      <ArrowLeftIcon className="w-3.5 h-3.5" />
      {label}
    </button>
  );
}

export function LoginPip() {
  return (
    <div className="w-11 h-11 rounded-xl grid place-items-center bg-brand-soft text-yellow-04">
      <EnvelopeIcon className="w-[22px] h-[22px]" />
    </div>
  );
}

export function Callout({ k, v }) {
  return (
    <div className="bg-white border border-line rounded-xl px-4 py-3.5">
      <span className="block font-display text-[11px] font-bold uppercase tracking-[0.12em] text-muted mb-1.5">
        {k}
      </span>
      <span className="font-display text-[15px] font-bold text-ink break-all">
        {v}
      </span>
    </div>
  );
}

export function BrandSoftHint({ children }) {
  return (
    <div
      className="flex gap-2.5 items-start px-3.5 py-3 rounded-xl text-[13px] leading-[1.4]
                 bg-brand-soft border border-[rgba(255,189,89,0.4)] text-yellow-04"
    >
      <InformationCircleIcon className="w-4 h-4 mt-0.5 shrink-0" />
      <span>{children}</span>
    </div>
  );
}

export function Foot({ children }) {
  return (
    <div
      className="px-6 sm:px-9 pt-4 pb-7 flex items-center justify-between gap-4
                 border-t border-line bg-paper-2 text-[13px] text-muted-2"
    >
      {children}
    </div>
  );
}
